import React, { memo } from 'react';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Tooltip } from '@mui/material';
import { Description } from '@mui/icons-material';
import Date from 'components/formaters/Date';
import { CarModel } from 'models/StockModel';
import { VoyageModel } from 'models/VoyageModel';
import useCarDocModals from 'modules/cars/hooks/useCarDocModals';
import {
    ecExpired,
    exportCertificateRequired,
    originalECRequired,
    priceConfirmationRequired,
} from 'modules/cars/utils/common';
import auth from 'recoil/atoms/auth';


type CarDocCellProps = {
    car: CarModel,
    voyage?: VoyageModel
}

const CarDocCell = ({car, voyage}: CarDocCellProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation('cars');
    const {
        openUploadExpCertModal,
        openUploadPriceConfirmationModal,
        openUploadVehicleInvoiceModal,
        openUploadVehicleCatalogModal,
        openUploadExplanatoryNoteModal,
        openUploadRepairEstimateModal,
    } = useCarDocModals();
    const navigate = useNavigate();

    const editable = my.permissions.has('cars update');
    const expired = ecExpired(car, voyage);
    const originalRequired = originalECRequired(car);

    const ecColor = car.documents.exportCertificate ? (
            expired ?
                'error' :
                (originalRequired ?
                        'warning' :
                        undefined
                )
        ) :
        'disabled';

    const mainDocument = exportCertificateRequired(car) ? (
        <Tooltip
            title={car.documents.exportCertificate ? <>
                Export certificate <br/>
                U: <Date date={car.documents.exportCertificate} short/> <br/>
                <Box
                    component="span"
                    sx={expired ? {
                        fontWeight: 'bold',
                        textShadow: '1px 1px white, -1px -1px white, -1px 1px white, 1px -1px white',
                        color: 'error.light',
                    } : undefined}>
                    V: <Date date={car.exportCertificate.validUntil} short/>
                </Box> <br/>
                {originalRequired && t('original_ec_required_short')}
            </> : t('upload_title', {value: 'Export certificate'})}
            placement="top"
        >
            <Description
                color={ecColor}
                onClick={car.documents.exportCertificate ? () => navigate(`/car/${car.id}?doc=export_certificate`) : () => editable && openUploadExpCertModal(car)}
            />
        </Tooltip>
    ) : (
        <>
            <Tooltip
                title={car.documents.vehicleInvoice ? <>
                    Vehicle invoice <br/>
                    U: <Date date={car.documents.vehicleInvoice} short/>
                </> : t('upload_title', {value: 'Vehicle invoice'})}
                placement="top"
            >
                <Description
                    color={car.documents.vehicleInvoice ? undefined : 'disabled'}
                    onClick={() => (editable || car.documents.vehicleInvoice) && openUploadVehicleInvoiceModal(car)}
                />
            </Tooltip>

            <Tooltip
                title={car.documents.vehicleCatalog ? <>
                    Vehicle catalog <br/>
                    U: <Date date={car.documents.vehicleCatalog} short/>
                </> : t('upload_title', {value: 'Vehicle catalog'})}
                placement="top"
            >
                <Description
                    color={car.documents.vehicleCatalog ? undefined : 'disabled'}
                    onClick={car.documents.vehicleCatalog ? () => navigate(`/car/${car.id}?doc=vehicle_catalog`) : () => editable && openUploadVehicleCatalogModal(car)}
                />
            </Tooltip>
        </>
    );

    return (
        <>
            {mainDocument}

            {priceConfirmationRequired(car) &&
                <Tooltip
                    title={car.documents.priceConfirmation ? <>
                        Price confirmation<br/>
                        U: <Date date={car.documents.priceConfirmation} short/>
                    </> : t('upload_title', {value: 'Price confirmation'})}
                    placement="top"
                >
                    <Description
                        color={car.documents.priceConfirmation ? undefined : 'disabled'}
                        onClick={() => openUploadPriceConfirmationModal(car)}
                    />
                </Tooltip>
            }

            {!!car.damaged &&
                <Tooltip
                    title={car.documents.repairEstimate ? <>
                        Repair Estimate<br/>
                        U: <Date date={car.documents.repairEstimate} short/>
                    </> : t('upload_title', {value: 'Repair Estimate'})}
                    placement="top"
                >
                    <Description
                        color={car.documents.repairEstimate ? undefined : 'disabled'}
                        onClick={() => (editable || car.documents.repairEstimate) && openUploadRepairEstimateModal(car)}
                    />
                </Tooltip>
            }

            {!!car.damaged &&
                <Tooltip
                    title={car.documents.explanatoryNote ? <>
                        Explanatory Note<br/>
                        U: <Date date={car.documents.explanatoryNote} short/>
                    </> : t('upload_title', {value: 'Explanatory Note'})}
                    placement="top"
                >
                    <Description
                        color={car.documents.explanatoryNote ? undefined : 'disabled'}
                        onClick={() => (editable || car.documents.explanatoryNote) && openUploadExplanatoryNoteModal(car)}
                    />
                </Tooltip>
            }
        </>
    );
};

export default memo(CarDocCell);
